import { useMemo } from 'react';
import { products } from '../../../mocks/products';
import ProductCard from '../../shop/components/ProductCard';

interface ProductGridProps {
  category: 'apparel' | 'footwear' | 'accessories';
  selectedSubcategory: string;
  selectedSizes: string[];
  selectedColors: string[];
  priceRange: [number, number];
  sortBy: string;
}

export default function ProductGrid({
  category,
  selectedSubcategory,
  selectedSizes,
  selectedColors,
  priceRange,
  sortBy,
}: ProductGridProps) {
  const filteredProducts = useMemo(() => {
    let filtered = products.filter((product) => product.category === category);

    // Filter by subcategory
    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter((product) => 
        product.subcategory?.toLowerCase() === selectedSubcategory.toLowerCase()
      );
    }

    // Filter by sizes
    if (selectedSizes.length > 0) {
      filtered = filtered.filter((product) =>
        product.sizes?.some((size) => selectedSizes.includes(size))
      );
    }

    // Filter by colors
    if (selectedColors.length > 0) {
      filtered = filtered.filter((product) =>
        product.colors?.some((color) => selectedColors.includes(color))
      );
    }

    // Filter by price range
    filtered = filtered.filter(
      (product) => product.price >= priceRange[0] && product.price <= priceRange[1]
    );

    // Sort products
    switch (sortBy) {
      case 'newest':
        filtered = [...filtered].sort((a, b) => {
          if (a.isNew && !b.isNew) return -1;
          if (!a.isNew && b.isNew) return 1;
          return 0;
        });
        break;
      case 'price-low':
        filtered = [...filtered].sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered = [...filtered].sort((a, b) => b.price - a.price);
        break;
      default:
        // featured - keep original order
        break;
    }

    return filtered;
  }, [category, selectedSubcategory, selectedSizes, selectedColors, priceRange, sortBy]);

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-gray-400">
          Showing <span className="text-white font-bold">{filteredProducts.length}</span> products
        </p>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-[#1A1A1A] rounded-full">
            <i className="ri-search-line text-4xl text-gray-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">No products found</h3>
          <p className="text-gray-400 mb-8">
            Try adjusting your filters to see more results
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
}